import { ChevronRight, ShoppingCart, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useDemo } from '../contexts/DemoContext';

import { ProductCard } from './ProductCard';

import { AnimateSpin } from '@/components/AnimateSpin';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/Carousel';
import { useAuth } from '@/contexts/AuthContext';
import { useGetProduct } from '@/hooks/useGetProduct';
import { useRecommendationProducts } from '@/hooks/useRecommendationProducts';
import { mountProductTags, priceFormatterV2 } from '@/lib/utils';

function Recommendation({ productId }: { productId: string }) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Demo.components.ProductPage',
  });

  const { recommendation, isLoading } = useRecommendationProducts(productId);

  if (isLoading) {
    return <AnimateSpin />;
  }

  return (
    <div>
      {recommendation?.shopTheLook && recommendation.shopTheLook.length > 0 && (
        <section className="mb-16 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 p-8">
          <div className="mb-4 flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-medium text-foreground">
              {t('shopTheLookTitle')}
            </h2>
          </div>

          <p className="mb-8 text-muted-foreground">
            {t('shopTheLookDescription')}
          </p>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {recommendation?.shopTheLook.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      )}

      {recommendation?.products && recommendation.products.length > 0 && (
        <section className="mb-16 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 p-8">
          <div className="mb-4 flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-medium text-foreground">
              {t('recommendationTitle')}
            </h2>
          </div>

          <p className="mb-8 text-muted-foreground">
            {t('recommendationDescription')}
          </p>

          <Carousel
            opts={{
              align: 'start',
              loop: false,
            }}
            className="w-full"
          >
            <CarouselContent>
              {recommendation?.products.map((product) => (
                <CarouselItem
                  key={product.id}
                  className="md:basis-1/2 lg:basis-1/4"
                >
                  <ProductCard product={product} />
                </CarouselItem>
              ))}
            </CarouselContent>

            <CarouselPrevious type="button" className="-left-3" />

            <CarouselNext type="button" className="-right-3" />
          </Carousel>
        </section>
      )}
    </div>
  );
}

export function ProductPage() {
  const { productId, setPage, setNavigationHistory } = useDemo();

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Demo.components.ProductPage',
  });

  const { user } = useAuth();

  const catalogId = Number.parseInt(productId);

  const { product } = useGetProduct(catalogId);

  useEffect(() => {
    if (product) {
      setNavigationHistory((value) => [
        ...value,
        `{ productView: '${product.name}' }`,
      ]);
    }
  }, [product, setNavigationHistory]);

  if (!product) {
    return null;
  }

  const tags = mountProductTags(product!);

  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* Breadcrumb */}
        <ul className="mb-8 flex items-center gap-2 text-sm text-muted-foreground">
          <li>
            <span className="text-foreground">Home</span>
          </li>

          <li>
            <ChevronRight className="h-4 w-4" />
          </li>

          <li>
            <span className="text-foreground">
              {product.visionCategoryName}
            </span>
          </li>

          <li>
            <ChevronRight className="h-4 w-4" />
          </li>

          <li>
            <span className="text-foreground">{product.name}</span>
          </li>
        </ul>

        {/* Product Details */}
        <div className="mb-16 grid grid-cols-1 gap-12 lg:grid-cols-2">
          {/* Product Image */}
          <div className="relative aspect-[4/5] overflow-hidden rounded-lg bg-muted/30">
            {tags && tags.length > 0 && (
              <ul className="absolute left-2 top-2 z-10">
                {tags.map((tag) => (
                  <li key={tag}>
                    <Badge className="bg-green-500">{tag}</Badge>
                  </li>
                ))}
              </ul>
            )}

            <img
              src={product.image1}
              alt={product.name}
              className="h-full w-full object-cover"
            />
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            <div className="flex-1">
              <h1 className="mb-4 text-3xl font-medium text-foreground md:text-4xl">
                {product.name}
              </h1>

              <div className="mb-6 text-3xl font-medium text-primary">
                {priceFormatterV2(
                  Number(product.salePrice),
                  user!.customer.language,
                  user!.customer.platform,
                )}
              </div>

              <div className="prose prose-sm mb-8 max-w-none">
                {product.description.split('\n\n').map((paragraph, index) => (
                  <p
                    key={index}
                    className="mb-4 leading-relaxed text-muted-foreground"
                  >
                    {paragraph.trim()}
                  </p>
                ))}
              </div>

              {/* Tags */}
              <div className="mb-8 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="min-w-24 text-sm text-muted-foreground">
                    {t('category')}:
                  </span>

                  <Badge variant="secondary">
                    {product.visionCategoryName}
                  </Badge>
                </div>

                <div className="hidden items-center gap-2">
                  <span className="min-w-24 text-sm text-muted-foreground">
                    {t('collection')}:
                  </span>
                  <Badge variant="secondary">
                    {product.merchandisingCollectionSlices}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-4 border-t border-border pt-6 sm:flex-row">
              <Button size="lg" className="flex-1 gap-2">
                <ShoppingCart className="h-5 w-5" />
                {t('addToCart')}
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={() => setPage('catalog')}
              >
                {t('backToCatolog')}
              </Button>
            </div>
          </div>
        </div>

        <Recommendation productId={product.productId} />
      </div>
    </main>
  );
}
